import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sb } from '../../supabase';
import AdminRouteGuard from '../admin/AdminRouteGuard';
import MaabarWordmark from '../MaabarWordmark';
import { fetchReadyCount } from '../../lib/consoleVerification';
import { fetchUnhandledCount } from '../../lib/consoleNotifications';
import { fetchOpenViolationCount } from '../../lib/consoleContactViolations';
import { MARKET_PREVIEW } from '../../lib/featureFlags';
import { fetchIsGrowthOwner } from '../../lib/growthAccess';
import '../../pages/admin2/console.css';

// ── Inline stroke icons (currentColor, 24-grid) ──────────────────────────────
const PATHS = {
  dashboard: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  suppliers: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01',
  quotations: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5',
  managed: 'M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.3 7L12 12l8.7-5M12 22V12',
  import: 'M12 3v10M8 9l4 4 4-4M20 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2',
  chat: 'M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.9-.9L3 21l1.9-5.1A8.4 8.4 0 1 1 21 11.5z',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  legacy: 'M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.4 2.6L3 8M3 3v5h5M12 7v5l3 2',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  menu: 'M3 6h18M3 12h18M3 18h18',
  chevron: 'M6 9l6 6 6-6',
  contacts: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.7 2z',
  funnel: 'M3 4h18l-7 9v5l-4 2v-7z',
  verification: 'M12 2l8 3.5V11c0 5-3.4 9.3-8 10.5C7.4 20.3 4 16 4 11V5.5zM9 12l2 2 4-4',
  leads: 'M4 4h11l5 5v11H4zM15 4v5h5M8 13h8M8 17h5',
  catalog: 'M4 5h16v14H4zM4 15l4-4 3 3 3-4 6 7M9 9.5a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0',
  campaign: 'M22 2L11 13M22 2l-7 20-4-9-9-4z',
  docs: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 15l2 2 4-4',
  growth: 'M3 3v18h18M7 15l4-4 3 3 6-7M16 7h4v4',
};
export function Icon({ name, size = 17, style }) {
  const d = PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }} aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// ── Navigation. Items with `live:false` are not built in this console yet — they
// open their current (legacy) equivalent so nothing is a dead end during the
// parallel build; each phase flips one to `live` on its new /admin2 route. ──────
const NAV = [
  { key: 'dashboard',  path: '/admin2/dashboard',  icon: 'dashboard',  ar: 'اليوم',          en: 'Today',           live: true },
  // ADDED 2026-11-01 (owner): Growth OS. Shown only when the server says
  // is_growth_owner() — not to every super_admin. The route checks the same.
  { key: 'growth',     path: '/admin2/growth',     icon: 'growth',     ar: 'النمو',          en: 'Growth',          live: true, ownerOnly: true },
  { key: 'suppliers',  path: '/admin2/suppliers',  icon: 'suppliers',  ar: 'الموردون',       en: 'Suppliers',       live: true },
  { key: 'quotations', path: '/admin2/quotations', icon: 'quotations', ar: 'طلبات التجار',   en: 'Trader Requests', live: true },
  { key: 'reviews',    path: '/admin2/reviews',    icon: 'verification', ar: 'المراجعات',    en: 'Reviews',         live: true, count: 'verification' },
  // ADDED 2026-10-11 (owner): every trader action lands here, so it is a place
  // you look twenty times a day — the bar this list sets — not a tool.
  { key: 'notifs',     path: '/admin2/notifications', icon: 'bell',     ar: 'الإشعارات',      en: 'Notifications',   live: true, count: 'events' },
];

// Everything else. Not deleted, not demoted in importance — moved out of the
// place you look twenty times a day.
//
// The sidebar had ELEVEN entries for what is a handful of recurring jobs, and
// the habit that produced it was: a new need arrives, add an entry. That does
// not scale for an operations console, and it was the owner's actual complaint
// on 2026-09-18. Four entries answer "what am I doing", and a job that grows
// becomes rows inside one of them rather than a twelfth entry.
//
// Each of these is still one click away, and every one is reachable from the
// primary four as well: a managed request opens from the trader-requests board,
// a catalog import from the reviews hub, a factory's contacts from its record.
const TOOLS = [
  { key: 'managed',          path: '/admin2/managed',          icon: 'managed',    ar: 'الطلبات المُدارة',    en: 'Managed Orders' },
  { key: 'request-fees',     path: '/admin2/request-fees',     icon: 'managed',    ar: 'رسوم تأكيد الطلب',    en: 'Request Fees' },
  { key: 'docs',             path: '/admin2/docs',             icon: 'docs',       ar: 'المستندات',           en: 'Documents' },
  { key: 'import',           path: '/admin2/import',           icon: 'import',     ar: 'استيراد كتالوج',      en: 'Import Catalog' },
  { key: 'catalog-qa',       path: '/admin2/catalog-qa',       icon: 'catalog',    ar: 'تدقيق منتجات المصانع', en: 'Catalog QA' },
  { key: 'featured',         path: '/admin2/featured',         icon: 'catalog',    ar: 'المنتجات المميّزة',    en: 'Featured Products' },
  { key: 'image-quality',    path: '/admin2/image-quality',    icon: 'catalog',    ar: 'جودة صور المنتجات',   en: 'Image Quality' },
  { key: 'leads',            path: '/admin2/leads',            icon: 'leads',      ar: 'دليل المصانع الصينية', en: 'CN Directory' },
  { key: 'claim-campaign',   path: '/admin2/claim-campaign',   icon: 'campaign',   ar: 'حملة المطالبات',      en: 'Claim Campaign' },
  { key: 'factory-contacts', path: '/admin2/factory-contacts', icon: 'contacts',   ar: 'تواصل المصانع',       en: 'Factory Contacts' },
  { key: 'funnel',           path: '/admin2/funnel',           icon: 'funnel',     ar: 'قمع المطالبات',       en: 'Claim Funnel' },
  { key: 'templates',        path: '/admin2/templates',        icon: 'quotations', ar: 'قوالب الرسائل',       en: 'Message Templates' },
  { key: 'traders',          path: '/admin2/traders',          icon: 'suppliers',  ar: 'التجار',              en: 'Traders' },
  { key: 'conversations',    path: '/admin2/conversations',    icon: 'chat',       ar: 'محادثات التجار',      en: 'Trader Conversations' },
  // HioBuy marketplace — only in a build with the market routes (MARKET_PREVIEW).
  ...(MARKET_PREVIEW ? [
    { key: 'market-orders',   path: '/admin2/market-orders',   icon: 'managed',    ar: 'طلبات السوق',         en: 'Market Orders' },
    { key: 'market-products', path: '/admin2/market-products', icon: 'catalog',    ar: 'مراجعة منتجات السوق', en: 'Market Products' },
    { key: 'market-weights',  path: '/admin2/market-weights',  icon: 'catalog',    ar: 'منتجات بدون وزن',     en: 'Missing Weights' },
    { key: 'market-sizes',    path: '/admin2/market-sizes',    icon: 'catalog',    ar: 'أحجام الشحن',         en: 'Shipping Sizes' },
    { key: 'market-carrier-rates', path: '/admin2/market-carrier-rates', icon: 'catalog', ar: 'أسعار شحن المرفوضة', en: 'Refused-goods Rates' },
    { key: 'market-safety',   path: '/admin2/market-safety',   icon: 'catalog',    ar: 'سلامة الكتالوج',      en: 'Catalog Safety' },
    { key: 'market-holds',    path: '/admin2/market-holds',    icon: 'catalog',    ar: 'منتجات محجوبة',       en: 'Hidden Products' },
    { key: 'market-staff-shipping', path: '/admin2/market-staff-shipping', icon: 'catalog', ar: 'تسعير الشحن بالفريق', en: 'Staff Shipping' },
    { key: 'market-waiting',  path: '/admin2/market-waiting',  icon: 'managed',    ar: 'الشحنات المنتظرة',     en: 'Waiting Shipments' },
    { key: 'market-inquiries', path: '/admin2/market-inquiries', icon: 'managed', ar: 'أسئلة للموردين', en: 'Supplier Questions' },
    { key: 'market-exclusives', path: '/admin2/market-exclusives', icon: 'catalog', ar: 'حصريات معبر',       en: 'MAABAR Exclusives' },
    { key: 'market-collections', path: '/admin2/market-collections', icon: 'catalog', ar: 'مجموعات الصفحة الرئيسية', en: 'Home Collections' },
    { key: 'market-variants', path: '/admin2/market-variants', icon: 'catalog', ar: 'طريقة عرض الخيارات', en: 'Variant display' },
    { key: 'market-ingestion', path: '/admin2/market-ingestion', icon: 'catalog',  ar: 'جلب المنتجات التلقائي', en: 'Catalog Ingestion' },
    { key: 'market-settings', path: '/admin2/market-settings', icon: 'catalog',    ar: 'إعدادات السوق',       en: 'Market Settings' },
  ] : []),
];

// Collapsed "current console" — the legacy marketplace, kept reachable, out of
// the primary flow (decision: factory_directory is the supplier; park the rest).
const LEGACY = [
  { path: '/admin/orders', ar: 'الطلبات', en: 'Orders' },
  { path: '/admin/payments', ar: 'المدفوعات', en: 'Payments' },
  { path: '/admin/support', ar: 'التذاكر', en: 'Support' },
  { path: '/admin/disputes', ar: 'النزاعات', en: 'Disputes' },
  { path: '/admin/referrals', ar: 'الإحالات', en: 'Referrals' },
  { path: '/admin/emails', ar: 'الإيميلات', en: 'Emails' },
  { path: '/admin/analytics', ar: 'التحليلات', en: 'Analytics' },
  { path: '/admin/settings', ar: 'الإعدادات', en: 'Settings' },
];

function Brand({ isAr }) {
  return (
    <div dir="ltr" style={{ userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', color: 'var(--ac-ink)' }}>
        <MaabarWordmark height={17} />
      </div>
      <div style={{ marginTop: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: isAr ? '0' : '0.2em', color: 'var(--ac-faint)', textTransform: 'uppercase', fontFamily: isAr ? 'var(--font-ar)' : undefined }}>
        {isAr ? 'لوحة الإدارة' : 'Admin Panel'}
      </div>
    </div>
  );
}

export default function ConsoleShell({ children, user, profile, lang, active }) {
  const nav = useNavigate();
  const location = useLocation();
  const isAr = lang === 'ar';
  const [drawer, setDrawer] = useState(false);
  const [menu, setMenu] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  // Open when the page you are on lives in there, so you are never looking at a
  // collapsed list that does not explain where you are.
  const [toolsOpen, setToolsOpen] = useState(() => TOOLS.some((t) => t.key === active));
  const [pendingVerifications, setPendingVerifications] = useState(null);
  const [unhandledEvents, setUnhandledEvents] = useState(null);
  const [growthOwner, setGrowthOwner] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchIsGrowthOwner().then((ok) => { if (alive) setGrowthOwner(ok); });
    return () => { alive = false; };
  }, [user?.id]);

  useEffect(() => { setDrawer(false); setMenu(false); }, [location.pathname]);

  // How many verification files are actually waiting. It is the one number an
  // admin needs before deciding what to open, so it rides in the nav rather
  // than only on the page itself. Head-only count, refreshed per navigation so
  // it settles right after a decision.
  useEffect(() => {
    let alive = true;
    fetchReadyCount()
      .then((n) => { if (alive) setPendingVerifications(n); })
      .catch(() => { if (alive) setPendingVerifications(null); });
    // What the Notifications feed's "Needs action" holds: trader actions nobody
    // has marked handled, plus contact violations nobody has reviewed (owner,
    // 2026-10-15). Each half fails quietly on its own, so one broken source
    // never hides the other's count.
    Promise.all([
      fetchUnhandledCount().catch(() => 0),
      fetchOpenViolationCount().catch(() => 0),
    ]).then(([events, violations]) => { if (alive) setUnhandledEvents(events + violations); });
    return () => { alive = false; };
  }, [location.pathname]);
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menu]);

  const go = (item) => { setDrawer(false); nav(item.live === false && item.legacy ? item.legacy : item.path); };
  const signOut = async () => { await sb.auth.signOut(); nav('/login'); };

  const name = profile?.full_name || profile?.email || 'Admin';
  const initial = (name || 'A').trim().charAt(0).toUpperCase();
  const roleRaw = (profile?.role || '').toLowerCase();
  const role = roleRaw === 'super_admin' ? (isAr ? 'المالك' : 'Owner')
    : roleRaw === 'admin' ? (isAr ? 'مشرفة' : 'Admin')
      : (profile?.role || '').replace('_', ' ');
  const current = NAV.find((n) => n.key === active) || TOOLS.find((n) => n.key === active);
  const pageLabel = current ? (isAr ? current.ar : current.en) : (isAr ? 'لوحة الإدارة' : 'Admin Panel');

  const Sidebar = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="ac-brand"><Brand isAr={isAr} /></div>
      <div role="navigation" className="ac-nav">
        {NAV.filter((item) => !item.ownerOnly || growthOwner).map((item) => (
          <button key={item.key} className={`ac-nav-item${active === item.key ? ' on' : ''}`} onClick={() => go(item)}
                  style={{ flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left',
                    ...(item.featured && active !== item.key ? { background: 'rgba(154,123,79,0.09)' } : {}) }}>
            <Icon name={item.icon} className="ac-nav-ico" style={{ opacity: active === item.key ? 1 : 0.82, color: item.featured && active !== item.key ? '#9A7B4F' : undefined }} />
            <span style={{ flex: 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', fontWeight: item.featured ? 700 : undefined }}>{isAr ? item.ar : item.en}</span>
            {item.count === 'verification' && pendingVerifications > 0 && (
              <span className="ac-nav-count">{pendingVerifications}</span>
            )}
            {item.count === 'events' && unhandledEvents > 0 && (
              <span className="ac-nav-count">{unhandledEvents}</span>
            )}
            {item.featured && <span style={{ fontSize: 10, color: '#9A7B4F' }}>★</span>}
            {item.live === false && <span className="ac-nav-arrow">{isAr ? '↗' : '↗'}</span>}
          </button>
        ))}

        <p className="ac-nav-group-label" style={{ textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'أدوات' : 'Tools'}</p>
        <button className="ac-nav-item" onClick={() => setToolsOpen((o) => !o)}
                style={{ flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left' }}>
          <Icon name="import" className="ac-nav-ico" />
          <span style={{ flex: 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? 'أدوات التشغيل' : 'Operations tools'}</span>
          <Icon name="chevron" size={13} style={{ opacity: 0.4, transform: toolsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {toolsOpen && TOOLS.map((t) => (
          <button key={t.key} className={`ac-nav-item${active === t.key ? ' on' : ''}`} onClick={() => go(t)}
                  style={{ flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left', minHeight: 34, fontSize: 12.5, paddingInlineStart: 30 }}>
            <span style={{ flex: 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? t.ar : t.en}</span>
          </button>
        ))}

        <p className="ac-nav-group-label" style={{ textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'النظام القديم' : 'Legacy'}</p>
        <button className="ac-nav-item" onClick={() => setLegacyOpen((o) => !o)}
                style={{ flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left' }}>
          <Icon name="legacy" className="ac-nav-ico" />
          <span style={{ flex: 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? 'الأدوات القديمة' : 'Old tools'}</span>
          <Icon name="chevron" size={13} style={{ opacity: 0.4, transform: legacyOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {legacyOpen && LEGACY.map((l) => (
          <button key={l.path} className="ac-nav-item" onClick={() => { setDrawer(false); nav(l.path); }}
                  style={{ flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left', minHeight: 34, fontSize: 12.5, paddingInlineStart: 30, color: 'var(--ac-faint)' }}>
            <span style={{ flex: 1, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? l.ar : l.en}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <AdminRouteGuard user={user} profile={profile} lang={lang}>
      <div className="ac" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="ac-shell">
          {drawer && <div className="ac-drawer-ov" onClick={() => setDrawer(false)} />}
          <aside className={`ac-sidebar ${isAr ? 'rtl' : 'ltr'}${drawer ? ' open' : ''}`}><Sidebar /></aside>

          <div className={`ac-main ${isAr ? 'rtl' : 'ltr'}`}>
            <header className="ac-topbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <button className="ac-hamburger" onClick={() => setDrawer(true)} aria-label={isAr ? 'القائمة' : 'Menu'}><Icon name="menu" size={20} /></button>
                <div className="ac-crumb">
                  <span className="ac-crumb-root" style={{ fontFamily: isAr ? 'var(--font-ar)' : undefined }}>{isAr ? 'لوحة الإدارة' : 'Admin Panel'}</span>
                  <span className="ac-crumb-sep">/</span>
                  <span className="ac-crumb-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{pageLabel}</span>
                </div>
              </div>
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button className="ac-account" onClick={() => setMenu((o) => !o)} style={{ flexDirection: isAr ? 'row-reverse' : 'row' }}>
                  <span className="ac-avatar">{initial}</span>
                  <span className="ac-account-meta" style={{ textAlign: isAr ? 'right' : 'left' }}>
                    <span className="ac-account-name" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{name}</span>
                    {role && <span className="ac-account-role" style={{ fontFamily: isAr ? 'var(--font-ar)' : undefined }}>{role}</span>}
                  </span>
                  <Icon name="chevron" size={14} style={{ opacity: 0.5 }} />
                </button>
                {menu && (
                  <div className="ac-menu" style={{ [isAr ? 'left' : 'right']: 0, textAlign: isAr ? 'right' : 'left' }}>
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--ac-line)' }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ac-ink)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{name}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ac-faint)' }}>{role}</p>
                    </div>
                    <button className="ac-menu-item" onClick={signOut} style={{ flexDirection: isAr ? 'row-reverse' : 'row', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      <Icon name="logout" size={16} /><span>{isAr ? 'تسجيل الخروج' : 'Sign out'}</span>
                    </button>
                  </div>
                )}
              </div>
            </header>
            <main>{children}</main>
          </div>
        </div>
      </div>
    </AdminRouteGuard>
  );
}
